import React, { useState, useEffect, useMemo, useRef } from "react";
import { Flex, Text, Select, IconButton, Button, Dialog, TextField, Badge } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { ActiveIntegrateCard } from "components";
import {
  useGetGoogleAdsMappings,
  useSaveGoogleAdsMappings,
  useDeactivateGoogleAds,
  useGetAllBrands,
  useGetGoogleAdsAccounts,
  useAddGoogleAdsAccounts,
} from "hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useProfileContext, useLocationContext } from "context";
import { useToast } from "../../../context/ToastContext";
import { AuthGoogleAdsDialog } from "./AuthGoogleAdsDialog";
import { useRemoveGoogleAdsAccount } from "hooks";
import { PermissionAlertDialog } from "components";

export function ActiveGoogleAdsIntegration() {
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { initialItems, locationsData } = useLocationContext();
  const { setToastMessage } = useToast();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const [isBrandsSaving, setIsBrandsSaving] = useState(false);
  const [isIndependentSaving, setIsIndependentSaving] = useState(false);

  const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrands(userProfile?.organisation_id);

  // stable mock hooks
  const { data: googleAccountsData, isLoading: isAccountsLoading } = useGetGoogleAdsAccounts(userProfile?.organisation_id);
  const { data: googleMappingsData, isLoading: isMappingsLoading } = useGetGoogleAdsMappings(userProfile?.organisation_id);

  const saveGoogleAdsMappings = useSaveGoogleAdsMappings();
  const deactivateGoogleAds = useDeactivateGoogleAds();
  const removeGoogleAdsAccount = useRemoveGoogleAdsAccount();
  const addGoogleAdsAccounts = useAddGoogleAdsAccounts();
  const [openAddAccountDialog, setOpenAddAccountDialog] = useState(false);
  const [googleAdsConfigs, setGoogleAdsConfigs] = useState([]);
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [removingAccountId, setRemovingAccountId] = useState(null);

  const [googleAccounts, setGoogleAccounts] = useState([]); // { id, label }
  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);

  const [locationMappings, setLocationMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});
  const originalLocationMappingsRef = useRef({});

  const [brandAccountSelections, setBrandAccountSelections] = useState({});

  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const isOwner = userProfile.user_role === "Owner";

  const normalizeVal = (v) => {
    if (v === null || typeof v === "undefined") return "";
    return String(v).trim();
  };


  useEffect(() => {
    if (!googleAccountsData) return;

    // Safely extract accounts array from API response
    const accounts = googleAccountsData?.data?.accounts || [];

    // Normalize each into { id, label }
    const formatted = accounts.map((acc, idx) => {
      const id = String(acc.google_ads_account_id ?? "");
      const pretty =
        id.length === 10
          ? `${id.slice(0, 3)}-${id.slice(3, 6)}-${id.slice(6)}`
          : id;
      return {
        id,
        label: pretty || id || `Account ${idx + 1}`,
      };
    });

    setGoogleAccounts(formatted);

  }, [googleAccountsData]);

  // Accounts section: prepare simple accounts list
  const accountsList = (googleAccountsData?.data?.accounts || []).map((a) => ({ id: a.google_ads_account_id }));

  // prepare brand & independent lists (identical logic to other pages)
  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = brandsData.find((b) => b.is_default);
      const nonDefault = brandsData.filter((b) => !b.is_default);
      setBconnectBrands(nonDefault || []);
      const defaultLocations = (locationsData || []).filter(
        (loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null
      );
      setIndependentLocations(defaultLocations || []);
    } else {
      setBconnectBrands([]);
      setIndependentLocations([]);
    }
  }, [brandsData, locationsData]);

  const brandLocationsMap = useMemo(() => {
    const map = {};
    if (!bconnectBrands) return map;
    bconnectBrands.forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(b.id));
      }
    });
    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  const allBrandOwnedLocations = useMemo(() => Object.values(brandLocationsMap).flat(), [brandLocationsMap]);

  // Hydrate server mappings: convert server payload to map and MERGE only when it changes.
  useEffect(() => {
    const serverMappingsArr = (googleMappingsData?.data?.mappings?.location_mappings) || [];

    // build a simple serverMap keyed by loc id
    const serverMap = {};
    serverMappingsArr.forEach((m) => {
      const key = String(m.bconnect_location_id);
      serverMap[key] = {
        google_ads_account_id: m.google_ads_account_id || "",
        customer_list_name: m.customer_list_name || "",
        isMapped: Boolean(m.google_ads_account_id && m.customer_list_name),
      };
    });

    // decide whether serverMap differs from originalLocationMappingsRef.current for the keys present
    let serverDifferent = false;
    Object.keys(serverMap).forEach((k) => {
      const curr = originalLocationMappingsRef.current[k] || { google_account_id: "", customer_list_name: "" };
      if (String(curr.google_account_id || "") !== String(serverMap[k].google_ads_account_id || "") ||
          String(curr.customer_list_name || "") !== String(serverMap[k].customer_list_name || "")) {
        serverDifferent = true;
      }
    });

    // If no change in server values for keys returned, skip merging entirely
    if (!serverDifferent) return;

    // Merge server values into local editable state but preserve unsaved edits
    setLocationMappings((prev = {}) => {
      const prevOrig = originalLocationMappingsRef.current || {};
      const next = { ...prev };

      Object.keys(serverMap).forEach((key) => {
        const serverEntry = serverMap[key];
        const prevEntry = prev[key];
        const prevOrigEntry = prevOrig[key] || { google_account_id: "", customer_list_name: "" };

        const prevAcct = String(prevEntry?.google_account_id || "");
        const prevList = String(prevEntry?.customer_list_name || "");
        const prevOrigAcct = String(prevOrigEntry.google_account_id || "");
        const prevOrigList = String(prevOrigEntry.customer_list_name || "");

        // If there's a local unsaved edit (prev differs from prevOrig), keep local.
        // Otherwise adopt the server value.
        if (!prevEntry || (prevAcct === prevOrigAcct && prevList === prevOrigList)) {
          const wouldBe = {
            google_account_id: serverEntry.google_ads_account_id,
            customer_list_name: serverEntry.customer_list_name,
            isMapped: serverEntry.isMapped,
          };
          const already = next[key] || {};
          if (
            String(already.google_account_id || "") !== String(wouldBe.google_account_id || "") ||
            String(already.customer_list_name || "") !== String(wouldBe.customer_list_name || "")
          ) {
            next[key] = wouldBe;
          }
        }
      });

      return next;
    });

    // Merge server into originals (overwrite keys returned by server)
    setOriginalLocationMappings((prev = {}) => {
      const merged = { ...prev };
      let changed = false;
      Object.keys(serverMap).forEach((k) => {
        const current = merged[k] || { google_account_id: "", customer_list_name: "" };
        const incoming = {
          google_account_id: serverMap[k].google_ads_account_id,
          customer_list_name: serverMap[k].customer_list_name,
        };
        if (
          String(current.google_account_id || "") !== String(incoming.google_account_id || "") ||
          String(current.customer_list_name || "") !== String(incoming.customer_list_name || "")
        ) {
          merged[k] = incoming;
          changed = true;
        }
      });
      if (changed) {
        originalLocationMappingsRef.current = merged;
        return merged;
      }
      return prev;
    });

  }, [googleMappingsData]);

  // Handlers
  const handleLocationAccountChange = (bconnectLocationId, accountId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { google_account_id: "", customer_list_name: "", isMapped: false };
      next[key] = {
        ...existing,
        google_account_id: accountId,
        isMapped: Boolean(accountId && existing.customer_list_name && String(existing.customer_list_name).trim()),
      };
      return next;
    });
  };

  const handleLocationCustomerListChange = (bconnectLocationId, listName) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { google_account_id: "", customer_list_name: "", isMapped: false };
      next[key] = {
        ...existing,
        customer_list_name: listName,
        isMapped: Boolean(existing.google_account_id && listName && String(listName).trim()),
      };
      return next;
    });
  };

  const handleBrandAccountSelectionChange = (brandId, accountId) => {
    setBrandAccountSelections((prev) => ({ ...(prev || {}), [String(brandId)]: accountId }));
  };

  const handleApplyBrandAccount = (brandId) => {
    const selected = brandAccountSelections[String(brandId)];
    if (!selected) return;
    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      (brandLocs || []).forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = next[key] || { google_account_id: "", customer_list_name: "", isMapped: false };
        next[key] = {
          ...existing,
          google_account_id: selected,
          isMapped: Boolean(selected && existing.customer_list_name && String(existing.customer_list_name).trim()),
        };
      });
      return next;
    });
  };

  const checkHalfFilled = (locs) => {
    return (locs || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || { google_account_id: "", customer_list_name: "" };
      const hasAccount = Boolean(normalizeVal(curr.google_account_id));
      const hasList = Boolean(normalizeVal(curr.customer_list_name));
      return (hasAccount && !hasList) || (!hasAccount && hasList);
    });
  };

  const hasBrandSectionChanges = useMemo(() => {
    const brandLocations = allBrandOwnedLocations || [];
    return (brandLocations || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { google_account_id: "", customer_list_name: "" };

      // require both present to consider a meaningful change (your original logic)
      if (!normalizeVal(curr.google_account_id) || !normalizeVal(curr.customer_list_name)) return false;

      return (
        normalizeVal(curr.google_account_id) !== normalizeVal(orig.google_account_id) ||
        normalizeVal(curr.customer_list_name) !== normalizeVal(orig.customer_list_name)
      );
    });
  }, [allBrandOwnedLocations, locationMappings, originalLocationMappings]);

  const hasIndependentLocationChanges = useMemo(() => {
    const indep = independentLocations || [];
    return (indep || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { google_account_id: "", customer_list_name: "" };
      if (!normalizeVal(curr.google_account_id) || !normalizeVal(curr.customer_list_name)) return false;
      return (
        normalizeVal(curr.google_account_id) !== normalizeVal(orig.google_account_id) ||
        normalizeVal(curr.customer_list_name) !== normalizeVal(orig.customer_list_name)
      );
    });
  }, [independentLocations, locationMappings, originalLocationMappings]);

  const hasBrandHalfFilled = useMemo(() => checkHalfFilled(Object.values(brandLocationsMap).flat()), [brandLocationsMap, locationMappings]);
  const hasIndependentHalfFilled = useMemo(() => checkHalfFilled(independentLocations), [independentLocations, locationMappings]);

  // Save handler
  const handleSaveLocationMappings = async (section) => {
    let selectedLocations = [];
    if (section === "brands") {
      selectedLocations = Object.values(brandLocationsMap).flat();
    } else if (section === "independent") {
      selectedLocations = independentLocations || [];
    } else {
      selectedLocations = [...(independentLocations || []), ...(Object.values(brandLocationsMap).flat() || [])];
    }

    const locationPayload = (selectedLocations || [])
      .map((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const curr = locationMappings[key] || {};
        const currAccount = curr.google_account_id || null;
        const currList = curr.customer_list_name || null;
        const orig = originalLocationMappings[key] || {};
        const origAccount = orig.google_account_id || null;
        const origList = orig.customer_list_name || null;

        if (!currAccount || !currList) return null;
        if (String(currAccount) === String(origAccount) && String(currList) === String(origList)) return null;

        return {
          bconnect_location_id: locId,
          google_ads_account_id: currAccount,
          customer_list_name: currList,
        };
      })
      .filter(Boolean);

    if (locationPayload.length === 0) {
      setToastMessage("No location changes to save");
      return;
    }

    const savingSetters = section === "brands" ? [setIsBrandsSaving] : section === "independent" ? [setIsIndependentSaving] : [setIsBrandsSaving, setIsIndependentSaving];
    savingSetters.forEach((s) => s(true));

    try {
      const payload = {
        organisation_id: userProfile.organisation_id,
        brand_mappings: [],
        location_mappings: locationPayload,
      };

      const result = await saveGoogleAdsMappings.mutateAsync(payload);

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      const newOrig = { ...originalLocationMappings };
      locationPayload.forEach((p) => {
        const key = String(p.bconnect_location_id);
        const normalizedAccount = normalizeVal(p.google_ads_account_id);
        const normalizedList = normalizeVal(p.customer_list_name);
        newOrig[key] = {
          google_account_id: normalizedAccount,
          customer_list_name: normalizedList,
        };
      });

      // update original state + ref
      setOriginalLocationMappings(newOrig);
      originalLocationMappingsRef.current = newOrig;

      // IMPORTANT: also make the editable locationMappings match the canonical saved values
      setLocationMappings((prev = {}) => {
        const next = { ...(prev || {}) };
        locationPayload.forEach((p) => {
          const key = String(p.bconnect_location_id);
          const normalizedAccount = normalizeVal(p.google_ads_account_id);
          const normalizedList = normalizeVal(p.customer_list_name);
          const existing = next[key] || {};
          next[key] = {
            ...existing,
            google_account_id: normalizedAccount,
            customer_list_name: normalizedList,
            isMapped: Boolean(normalizedAccount && normalizedList),
          };
        });
        return next;
      });

      queryClient.invalidateQueries(["google-ads-mappings"]);
      setToastMessage("Location mappings saved successfully");
    } catch (err) {
      console.error("Error saving Google Ads mappings:", err);
      setToastMessage(err?.message || "Failed to save location mappings");
    } finally {
      savingSetters.forEach((s) => s(false));
    }
  };

  // Handle disconnect click with permission check
  const handleDisconnectClick = () => {
    if (isOwner) {
      setShowConfirmDialog(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  // Disconnect handler
  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);
      console.log("Deactivating Google Ads integration");
      const result = await deactivateGoogleAds.mutateAsync({ organisation_id: userProfile.organisation_id });
      if (result?.data?.status === "success") {
        queryClient.invalidateQueries(["google-ads-integration-status"]);
        queryClient.invalidateQueries(["google-ads-accounts"]);
        queryClient.invalidateQueries(["google-ads-mappings"]);
        setToastMessage("Google Ads disconnected");
      } else {
        throw new Error(result?.data?.message || "Failed to disconnect Google Ads integration");
      }
    } catch (err) {
      console.error("Error disconnecting Google Ads:", err);
      setToastMessage(err?.message || "Failed to disconnect Google Ads");
    } finally {
      setIsDeactivating(false);
      setShowConfirmDialog(false);
    }
  };

  // Handle adding accounts from the Auth dialog
  const handleAddAccountsContinue = async () => {
    const queryKey = ["google-ads-accounts", userProfile.organisation_id];
    const newConfigs = googleAdsConfigs || [];

    if (newConfigs.length === 0) {
      setOpenAddAccountDialog(false);
      return;
    }

    // Optimistic update
    await queryClient.cancelQueries({ queryKey });
    const previousAccountsData = queryClient.getQueryData(queryKey);

    queryClient.setQueryData(queryKey, (oldData) => {
      const existingAccounts = oldData?.data?.accounts || [];
      const newAccounts = newConfigs.map(config => ({ 
        google_ads_account_id: config.googleAdsAccountId,
        // Add other fields if your API response has them, otherwise default
      }));
      
      return {
        ...oldData,
        data: {
          ...oldData?.data,
          accounts: [...existingAccounts, ...newAccounts],
        },
      };
    });

    setOpenAddAccountDialog(false);
    setGoogleAdsConfigs([]);
    setIsAddLoading(true);

    try {
      const payload = {
        organisation_id: userProfile.organisation_id,
        configs: newConfigs.map(({ googleAdsAccountId }) => ({
          google_ads_account_id: googleAdsAccountId,
        })),
      };

      const res = await addGoogleAdsAccounts.mutateAsync(payload);
      if (res?.data?.status !== "success") {
        queryClient.setQueryData(queryKey, previousAccountsData); // Rollback
        setToastMessage(res?.data?.message || "Failed to add Google Ads account(s)");
      } else {
        setToastMessage("Google Ads account(s) added");
      }
    } catch (err) {
      queryClient.setQueryData(queryKey, previousAccountsData); // Rollback
      console.error("Error adding accounts", err);
      setToastMessage(err?.message || "Failed to add Google Ads account(s)");
    } finally {
      setIsAddLoading(false);
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries(["google-ads-mappings"]);
      queryClient.invalidateQueries(["google-ads-integration-status"]);
    }
  };

  if (!initialItems) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>No locations available.</Text>
      </Flex>
    );
  }

  const loading = isBrandsLoading || isAccountsLoading || isMappingsLoading;
  if (loading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading Google Ads integration data...</Text>
      </Flex>
    );
  }

  const accountLabel = (id) => {
    if (!id) return "Select account...";
    const found = (googleAccounts || []).find((c) => String(c.id) === String(id));
    return found ? found.label : String(id);
  };

  const colStyle = { width: "32%", minWidth: 0 };

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Google Ads"
        subtitle="Map locations to Google Ads accounts & customer lists"
        image_url="google.png"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        {/* BRANDS section */}
        <ActiveIntegrateCard.Section name="Accounts" subtitle="Manage connected Google Ads accounts">
          <Flex direction="column" gap="3" style={{ width: "100%" }}>
            <Flex justify="between" align="center">
              <Text size="2" weight="medium">Connected accounts</Text>
              <Button size="2" variant="soft" disabled={!isOwner} onClick={() => setOpenAddAccountDialog(true)}>Add account</Button>
            </Flex>

            {(accountsList || []).length > 0 ? (
              (accountsList || []).map((acc) => (
                <Flex key={acc.id} direction="row" justify="between" align="center" style={{ padding: "6px 8px", border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-2)" }}>
                  <Text size="2" style={{ fontFamily: "monospace" }}>
                    {acc.id.length === 10
                      ? `${acc.id.slice(0, 3)}-${acc.id.slice(3, 6)}-${acc.id.slice(6)}`
                      : acc.id}
                  </Text>
                  <Flex gap="2" style={{ marginLeft: 8 }}>
                    <Button size="1" variant="soft" color="red" 
                    loading={removingAccountId === acc.id}
                    disabled={!isOwner}
                    onClick={async () => {
                      const queryKey = ["google-ads-accounts", userProfile.organisation_id];
                      
                      // Optimistic update
                      await queryClient.cancelQueries({ queryKey });
                      const previousAccountsData = queryClient.getQueryData(queryKey);
                      queryClient.setQueryData(queryKey, (oldData) => {
                        if (!oldData || !oldData.data || !oldData.data.accounts) return oldData;
                        const newAccounts = oldData.data.accounts.filter(
                          (account) => String(account.google_ads_account_id) !== String(acc.id)
                        );
                        return { ...oldData, data: { ...oldData.data, accounts: newAccounts } };
                      });

                      // Manually clear mappings for the removed account
                      setLocationMappings(prev => {
                        const next = {...prev};
                        Object.keys(next).forEach(locId => {
                          if (String(next[locId].google_account_id) === String(acc.id)) {
                            next[locId].google_account_id = "";
                            next[locId].customer_list_name = "";
                            next[locId].isMapped = false;
                          }
                        });
                        return next;
                      });

                      // ALSO clear original mappings to allow re-adding
                      setOriginalLocationMappings(prev => {
                        const next = {...prev};
                        Object.keys(next).forEach(locId => {
                            if (String(next[locId].google_account_id) === String(acc.id)) {
                                next[locId].google_account_id = "";
                                next[locId].customer_list_name = "";
                            }
                        });
                        originalLocationMappingsRef.current = next; // Update the ref as well
                        return next;
                      });

                      setRemovingAccountId(acc.id);

                      try {
                        const res = await removeGoogleAdsAccount.mutateAsync({ organisation_id: userProfile.organisation_id, google_ads_account_id: acc.id });
                        if (res?.data?.status !== "success") {
                           // Rollback on API error
                           queryClient.setQueryData(queryKey, previousAccountsData);
                           setToastMessage(res?.data?.message || "Failed to remove account");
                        } else {
                          setToastMessage("Google Ads account removed");
                        }
                      } catch (err) {
                        // Rollback on network/unexpected error
                        queryClient.setQueryData(queryKey, previousAccountsData);
                        console.error("Error removing account", err);
                        setToastMessage(err?.message || "Failed to remove account");
                      } finally {
                        setRemovingAccountId(null);
                        queryClient.invalidateQueries({ queryKey });
                        queryClient.invalidateQueries(["google-ads-mappings"]);
                      }
                    }}>Remove</Button>
                  </Flex>
                </Flex>
              ))
            ) : (
              <Flex justify="center"><Text>No connected accounts</Text></Flex>
            )}
          </Flex>
        </ActiveIntegrateCard.Section>
        <ActiveIntegrateCard.Section name="Brands" subtitle="Group: b connect brands with each of their locations" note="You only need to complete rows for the locations you want to integrate. You can leave the rest blank.">
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={colStyle}>b connect location</Text>
              <Text size="2" weight="medium" style={{ ...colStyle, paddingLeft: "8px" }}>Google Ads account</Text>
              <Text size="2" weight="medium" style={{ ...colStyle, paddingLeft: "13px" }}>Customer list name</Text>
            </Flex>

            {(bconnectBrands || []).length > 0 ? (
              (bconnectBrands || []).map((b) => {
                const brandLocations = brandLocationsMap[b.id] || [];
                return (
                  <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                    <Flex direction="row" style={{ width: "100%", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                      <Badge size="2" variant="soft" style={{flexShrink: 0, flexGrow: 0, width: "auto", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} title={b.name}>
                        {b.name}
                      </Badge>

                      {(brandLocationsMap[b.id] || []).length > 0 && (
                        <Flex style={{ marginLeft: "8px", gap: 8, alignItems: "center", flexShrink: 0 }}>
                          <Select.Root
                            value={brandAccountSelections[String(b.id)] || ""}
                            onValueChange={(v) => handleBrandAccountSelectionChange(b.id, v)}
                            disabled={!isOwner}
                            style={{ width: 220, minWidth: 120, flexShrink: 0 }}
                          >
                            <Select.Trigger placeholder="Apply account to brand..." style={{ width: "220px", boxSizing: "border-box" }}>
                              <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={brandAccountSelections[String(b.id)] ? accountLabel(brandAccountSelections[String(b.id)]) : "Apply account to brand..."}>
                                {brandAccountSelections[String(b.id)] ? accountLabel(brandAccountSelections[String(b.id)]) : "Apply account to brand..."}
                              </div>
                            </Select.Trigger>
                            <Select.Content side="bottom" align="start" sideOffset={4}>
                              {(googleAccounts || []).map((gc) => <Select.Item key={gc.id} value={gc.id} title={gc.label}>{gc.label}</Select.Item>)}
                            </Select.Content>
                          </Select.Root>

                          <Button size="1" variant="soft" onClick={() => handleApplyBrandAccount(b.id)} disabled={!isOwner || !brandAccountSelections[String(b.id)]} style={{ flexShrink: 0 }}>
                            Apply
                          </Button>
                        </Flex>
                      )}
                    </Flex>

                    {(brandLocations || []).length > 0 ? (
                      brandLocations.map((loc) => {
                        const locId = loc.id ?? loc.value ?? loc.location_id;
                        const key = String(locId);
                        const mapping = locationMappings[key] || {};
                        return (
                          <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                            <Flex style={colStyle}>
                              <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                  {loc.name ?? loc.label ?? ""}
                                </Text>
                              </Flex>
                            </Flex>

                            <Flex style={{ paddingLeft: "8px", paddingRight: "8px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <ArrowRightIcon width="16" height="16" />
                              </IconButton>
                            </Flex>

                            <Flex style={{ ...colStyle }}>
                              <Select.Root value={mapping.google_account_id || ""} onValueChange={(v) => handleLocationAccountChange(key, v)} disabled={!isOwner} style={{ width: "100%" }}>
                                <Select.Trigger placeholder="Select account..." style={{ width: "100%", boxSizing: "border-box" }}>
                                  <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.google_account_id ? accountLabel(mapping.google_account_id) : "Select a Google Ads account"}>
                                    {mapping.google_account_id ? accountLabel(mapping.google_account_id) : "Select a Google Ads account"}
                                  </div>
                                </Select.Trigger>
                                <Select.Content side="bottom" align="start" sideOffset={4}>
                                  {(googleAccounts || []).map((gc) => <Select.Item key={gc.id} value={gc.id} title={gc.label}>{gc.label}</Select.Item>)}
                                </Select.Content>
                              </Select.Root>
                            </Flex>

                            <Flex style={{ paddingLeft: "8px", paddingRight: "8px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <ArrowRightIcon width="16" height="16" />
                              </IconButton>
                            </Flex>

                            <Flex style={colStyle}>
                              <TextField.Root
                                value={mapping.customer_list_name || ""}
                                onChange={(e) => handleLocationCustomerListChange(key, e.target.value)}
                                disabled={!isOwner}
                                placeholder="Enter customer list name"
                                title={mapping.customer_list_name || ""}
                                style={{
                                  width: "100%",
                                  textOverflow: "ellipsis",
                                  overflowX: "auto",
                                  whiteSpace: "nowrap",
                                }}
                              />
                            </Flex>
                          </Flex>
                        );
                      })
                    ) : (
                      <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                        <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                      </Flex>
                    )}
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No brands found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("brands")}
                loading={isBrandsSaving || isIndependentSaving}
                disabled={!isOwner || !hasBrandSectionChanges || hasBrandHalfFilled || isBrandsSaving || isIndependentSaving}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>

        {/* INDEPENDENT LOCATIONS */}
        <ActiveIntegrateCard.Section name="Locations" subtitle="Map independent b connect locations to a Google Ads account & customer list name." note="You only need to complete rows for the locations you want to integrate. You can leave the rest blank.">
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={colStyle}>b connect location</Text>
              <Text size="2" weight="medium" style={{ ...colStyle, paddingLeft: "8px" }}>Google Ads account</Text>
              <Text size="2" weight="medium" style={{ ...colStyle, paddingLeft: "13px" }}>Customer list name</Text>
            </Flex>

            {(independentLocations || []).length > 0 ? (
              (independentLocations || []).map((loc) => {
                const locId = loc.id ?? loc.value ?? loc.location_id;
                const key = String(locId);
                const mapping = locationMappings[key] || {};
                return (
                  <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                    <Flex style={colStyle}>
                      <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                        <Text size="2" title={loc.name} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                          {loc.name}
                        </Text>
                      </Flex>
                    </Flex>

                    <Flex style={{ paddingLeft: "8px", paddingRight: "8px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={{ ...colStyle }}>
                      <Select.Root value={mapping.google_account_id || ""} onValueChange={(v) => handleLocationAccountChange(key, v)} disabled={!isOwner} style={{ width: "100%" }}>
                        <Select.Trigger placeholder="Select account..." style={{ width: "100%", boxSizing: "border-box" }}>
                          <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.google_account_id ? accountLabel(mapping.google_account_id) : "Select a Google Ads account"}>
                            {mapping.google_account_id ? accountLabel(mapping.google_account_id) : "Select a Google Ads account"}
                          </div>
                        </Select.Trigger>
                        <Select.Content side="bottom" align="start" sideOffset={4}>
                          {(googleAccounts || []).map((gc) => <Select.Item key={gc.id} value={gc.id} title={gc.label}>{gc.label}</Select.Item>)}
                        </Select.Content>
                      </Select.Root>
                    </Flex>

                    <Flex style={{ paddingLeft: "8px", paddingRight: "8px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={colStyle}>
                      <TextField.Root
                        value={mapping.customer_list_name || ""}
                        onChange={(e) => handleLocationCustomerListChange(key, e.target.value)}
                        disabled={!isOwner}
                        placeholder="Enter customer list name"
                        title={mapping.customer_list_name || ""}
                        style={{
                          width: "100%",
                          textOverflow: "ellipsis",
                          overflowX: "auto",
                          whiteSpace: "nowrap",
                        }}
                      />
                    </Flex>
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No locations found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("independent")}
                loading={isIndependentSaving || isBrandsSaving}
                disabled={!isOwner || !hasIndependentLocationChanges || hasIndependentHalfFilled || isIndependentSaving || isBrandsSaving}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      {/* Add account dialog */}
      <AuthGoogleAdsDialog
        isOpen={openAddAccountDialog}
        onOpenChange={setOpenAddAccountDialog}
        onContinue={handleAddAccountsContinue}
        googleAdsConfigs={googleAdsConfigs}
        setGoogleAdsConfigs={setGoogleAdsConfigs}
        isLoading={isAddLoading}
      />

      {/* Confirm dialog */}
      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect Google Ads</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Customer data will no longer sync with Google Ads. To complete the disconnection, remove the b connect's 
            manager account access in your Google Ads account settings. Are you sure you want to disconnect?
          </Dialog.Description>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" color="red" onClick={handleDeactivate} loading={isDeactivating} disabled={isDeactivating}>Disconnect</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Permission Alert Dialog */}
      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
